import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EventSubscription, EventSubscriptionRequest } from '@frontend/sports/event-subscription/feature/base-subscription';
import { EventSubscriptionService } from '@frontend/sports/event-subscription/feature/event-subscription';
import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { difference, omit, values } from 'lodash-es';
import { Observable, Subject, asyncScheduler, buffer, distinct, filter, map, noop, throttleTime } from 'rxjs';

import { FreshDataType } from '../../data-refresh/fresh-data-provider.service';
import { FreshDataService } from '../../data-refresh/fresh-data.service';

@Injectable({ providedIn: 'root' })
export class LiveEventsNavigationSubscriptionService {
    private subscription?: EventSubscription<EventModel>;
    private events: { [id: string]: EventModel } = {};

    private eventsStream$ = new Subject<EventModel>();
    readonly updatedEvents$: Observable<EventModel[]>;

    constructor(
        private eventSubscriptionService: EventSubscriptionService,
        freshDataService: FreshDataService,
    ) {
        const updateQueueInMilliseconds = 400;

        const flush$ = this.eventsStream$.pipe(map(noop), throttleTime(updateQueueInMilliseconds, asyncScheduler));

        // buffer updates and notify at once for performance reasons
        this.updatedEvents$ = this.eventsStream$.pipe(
            distinct((event) => event.id, flush$),
            buffer(flush$),
            filter((events) => events && events.length > 0),
            takeUntilDestroyed(),
        );

        freshDataService.reloadNeeded
            .pipe(filter((type) => type === FreshDataType.DataAndPush))
            .pipe(filterSportsEmitLast())
            .subscribe(() => {
                this.onPushReconnected();
            });
    }

    refreshSubscriptions(events: EventModel[]): void {
        if (this.subscription) {
            const unsubscribe = difference(
                Object.keys(this.events),
                events.map((event) => event.id),
            );
            this.subscription.remove(unsubscribe);
            this.events = omit(this.events, unsubscribe);
        }

        this.subscribe(events);
    }

    subscribe(events: EventModel[]): void {
        const request: EventSubscriptionRequest<EventModel>[] = [];

        for (const event of events) {
            if (!this.events[event.id]) {
                this.events[event.id] = event;
                request.push({ event, callback: this.handler });
            }
        }

        if (request.length === 0) {
            return;
        }

        const subscription = this.eventSubscriptionService.subscribe(request);

        if (this.subscription) {
            this.subscription = this.subscription.add(subscription);
        } else {
            this.subscription = subscription;
        }
    }

    unsubscribe(eventsToUnsubscribe: EventModel[]): void {
        if (eventsToUnsubscribe && eventsToUnsubscribe.length && this.subscription) {
            this.subscription.remove(eventsToUnsubscribe);

            eventsToUnsubscribe.forEach((e) => {
                delete this.events[e.id];
            });
        }
    }

    unsubscribeAll(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }

        this.events = {};
    }

    private handler = (event: EventModel) => {
        if (this.events[event.id]) {
            this.eventsStream$.next(event);
        }
    };

    private onPushReconnected = () => {
        const resubscribe = values(this.events);

        this.events = {};
        delete this.subscription;

        this.subscribe(resubscribe);
    };
}
